import { useSyncExternalStore } from 'react'

import type { Api } from '~/api/endpoints'
import { entryBody, type Entry } from '~/data/nutrition'
import { keepEntries, logsSent, reloadNutrition } from '~/data/nutrition-store'

/**
 * Undoing, for the diary — see the nutrition spec, Undoing. Everything the diary does to
 * entries can be undone, and done again after being undone.
 *
 * The stack lives in memory and nowhere else, so it goes when the app is closed. That is the
 * spec's own answer and it is the honest one: an undo kept across a reload would offer to put
 * back a row somebody else may have moved since, and the diary has no way of telling.
 *
 * One press is one step, however many entries it changed — copying five entries to tomorrow is
 * one thing done, so it is one thing undone.
 */

/** How far back it goes. The oldest step falls off the end rather than the newest being refused. */
const LIMIT = 50

/**
 * What one entry looked like before a press and after it. `null` on either side is the entry
 * not existing then: before-null is one logged, after-null is one removed, and both filled is
 * one edited or moved — which is the same shape, since a move is an entry written elsewhere.
 */
export interface EntryChange {
  readonly id: string
  readonly before: Entry | null
  readonly after: Entry | null
}

interface Step {
  readonly said: string
  readonly changes: readonly EntryChange[]
}

let done: Step[] = []
let undone: Step[] = []

const listeners = new Set<() => void>()
/** A new object each time anything moves, so `useSyncExternalStore` sees the change. */
let snapshot = { canUndo: false, canRedo: false }

function said(): void {
  snapshot = { canUndo: done.length > 0, canRedo: undone.length > 0 }
  for (const listener of listeners) {
    listener()
  }
}

/** Everything forgotten — what signing out does, since the next account is not this one. */
export function forgetDiarySteps(): void {
  done = []
  undone = []
  said()
}

/**
 * One press remembered. Doing something new is the end of whatever had been undone: there is
 * no way back to a future that was written over.
 */
export function recordDiaryStep(step: { said: string; changes: readonly EntryChange[] }): void {
  if (step.changes.length === 0) {
    return
  }
  done = [...done, { said: step.said, changes: step.changes }].slice(-LIMIT)
  undone = []
  said()
}

/**
 * Puts each entry back the way one side of the change had it — all of them together.
 *
 * One press is one step, so undoing it is one movement on the screen. Sent and laid in one at
 * a time, the five entries of a removal came back one after another, which reads as five
 * things happening rather than as the one thing being undone. They go out together and land
 * together: nothing is laid into the mirror until the server has taken all of them.
 */
async function applyEntries(api: Api, entries: readonly (readonly [string, Entry | null])[]) {
  await logsSent()
  const gone = entries.filter(([, held]) => held === null).map(([id]) => id)
  const back = entries.flatMap(([, held]) => (held === null ? [] : [held]))
  const saved = await Promise.all(back.map((entry) => api.putEntry(entry.id, entryBody(entry))))
  if (gone.length > 0) {
    await api.deleteEntries(gone)
  }
  keepEntries(saved, gone)
  reloadNutrition()
}

/** Undoes the last thing done, and says what it was. Null where there was nothing to undo. */
export async function undoDiaryStep(api: Api): Promise<string | null> {
  const step = done[done.length - 1]
  if (step === undefined) {
    return null
  }
  await applyEntries(
    api,
    step.changes.map((change) => [change.id, change.before] as const),
  )
  done = done.slice(0, -1)
  undone = [...undone, step]
  said()
  return step.said
}

/** Does again the last thing undone, and says what it was. */
export async function redoDiaryStep(api: Api): Promise<string | null> {
  const step = undone[undone.length - 1]
  if (step === undefined) {
    return null
  }
  await applyEntries(
    api,
    step.changes.map((change) => [change.id, change.after] as const),
  )
  undone = undone.slice(0, -1)
  done = [...done, step].slice(-LIMIT)
  said()
  return step.said
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Whether there is anything to undo, and anything to do again. */
export function useDiarySteps(): { canUndo: boolean; canRedo: boolean } {
  return useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => snapshot,
  )
}
